// Vocabolario delle colonne configurabili del cruscotto (`gestore.dashboard_columns`).
//
// Vive in `constants/` perché è condiviso tra due domini isolati da
// `eslint-plugin-boundaries`: `features/home/control` (che rende le colonne) e
// `features/clients/managers` (la scheda che le configura). Qui stanno solo
// costanti pure — nessun JSX, nessun renderer.

/**
 * Colonne standard della griglia ordini del cruscotto. `field` è la chiave di
 * join con le colonne costruite da `buildStandardColumns` (features/home/control):
 * l'allineamento tra le due liste è verificato da un test.
 */
export const ORDERS_STANDARD_COLUMNS = [
  { field: 'autista', labelKey: 'autista', defaultWidth: 200 },
  { field: 'service_mode', labelKey: 'service_mode', defaultWidth: 150 },
  { field: 'status', labelKey: 'stato', defaultWidth: 200 },
  { field: 'cliente', labelKey: 'cliente', defaultWidth: 200 },
  { field: 'data_esecuzione', labelKey: 'data_ora', defaultWidth: 250 },
  { field: 'indirizzo_da', labelKey: 'viaggio', defaultWidth: 260 },
  { field: 'prezzo', labelKey: 'prezzo', defaultWidth: 160 },
  { field: 'fermo_auto', labelKey: 'fermo_auto', defaultWidth: 120 },
  { field: 'dettagli', labelKey: 'dettagli', defaultWidth: 150 },
  { field: 'actions', labelKey: 'azioni', defaultWidth: 200 },
];

export const STANDARD_COLUMN_FIELDS = ORDERS_STANDARD_COLUMNS.map((c) => c.field);

/**
 * Tipi di colonna custom. Oltre ai formati "grezzi" (text/number/boolean) ci
 * sono i tipi semantici, che riusano le celle condivise di `@ui/table_cells`
 * per avere lo stesso aspetto delle colonne standard.
 */
export const COLUMN_TYPES = [
  { value: 'text', labelKey: 'col_type_text', defaultValue: 'Testo' },
  { value: 'number', labelKey: 'col_type_number', defaultValue: 'Numero' },
  { value: 'currency', labelKey: 'col_type_currency', defaultValue: 'Prezzo (€)' },
  { value: 'date', labelKey: 'col_type_date', defaultValue: 'Data' },
  { value: 'datetime', labelKey: 'col_type_datetime', defaultValue: 'Data e ora' },
  { value: 'boolean', labelKey: 'col_type_boolean', defaultValue: 'Sì / No' },
  { value: 'passenger', labelKey: 'col_type_passenger', defaultValue: 'Passeggero' },
  { value: 'route', labelKey: 'col_type_route', defaultValue: 'Viaggio (da → a)' },
  { value: 'template', labelKey: 'col_type_template', defaultValue: 'Template calcolato' },
  { value: 'preset', labelKey: 'col_type_preset', defaultValue: 'Renderer predefinito' },
];

/** Tipi che leggono un secondo campo dato (`field_to`). */
export const TWO_FIELD_TYPES = ['route'];

/**
 * Renderer predefiniti selezionabili con `type: 'preset'`. Le chiavi devono
 * esistere nella mappa `presets` iniettata dal consumer (ORDERS_COLUMN_PRESETS
 * in features/home/control). Sono anche i valori scritti dalla config legacy
 * Trenord nel campo `renderCell`.
 */
export const COLUMN_PRESETS = [
  { value: 'trenordStatus', label: 'Trenord · Stato' },
  { value: 'trenordRete', label: 'Trenord · Rete' },
  { value: 'trenordElencoMateriali', label: 'Trenord · Elenco materiali' },
  { value: 'trenordPasseggero', label: 'Trenord · Passeggero' },
  { value: 'trenordProvvedimentiCircolazione', label: 'Trenord · Provvedimenti circolazione' },
  { value: 'trenordInserimento', label: 'Trenord · Data inserimento' },
  { value: 'trenordDataViaggio', label: 'Trenord · Data viaggio' },
  { value: 'trenordViaggio', label: 'Trenord · Viaggio' },
  { value: 'trenordAssistenza', label: 'Trenord · Assistenza' },
  { value: 'trenordInvalidita', label: 'Trenord · Invalidità' },
  { value: 'trenordActions', label: 'Trenord · Azioni' },
];

export const COLUMN_ALIGNS = ['left', 'center', 'right'];
export const COLUMN_WEIGHTS = ['normal', 'bold'];
export const COLUMN_DISPLAYS = ['text', 'chip'];

/**
 * Colori selezionabili: nomi di design token (`tokens.css`), mai hex liberi.
 * `null` = colore ereditato dalla griglia.
 */
export const COLUMN_COLOR_TOKENS = [
  '--color-text',
  '--color-text-muted',
  '--color-primary',
  '--color-teal',
  '--color-success',
  '--color-warning',
  '--color-danger',
  '--color-info',
  '--color-purple',
  '--color-pink',
  '--color-neutral',
];

/** Sfondi: le varianti "container" (tenui) + le superfici neutre. */
export const COLUMN_BACKGROUND_TOKENS = [
  '--color-surface-dim',
  '--color-primary-container',
  '--color-teal-container',
  '--color-success-container',
  '--color-warning-container',
  '--color-danger-container',
  '--color-info-container',
  '--color-purple-container',
  '--color-pink-container',
  '--color-neutral-container',
];

export const CHIP_VARIANTS = [
  'neutral',
  'teal',
  'success',
  'warning',
  'danger',
  'info',
  'purple',
  'pink',
  'primary',
];

/** Operatori delle regole condizionali di stile. `value` non serve per empty/not_empty. */
export const RULE_OPS = [
  { value: 'eq', label: 'uguale a', needsValue: true },
  { value: 'neq', label: 'diverso da', needsValue: true },
  { value: 'contains', label: 'contiene', needsValue: true },
  { value: 'gt', label: 'maggiore di', needsValue: true },
  { value: 'lt', label: 'minore di', needsValue: true },
  { value: 'empty', label: 'vuoto', needsValue: false },
  { value: 'not_empty', label: 'valorizzato', needsValue: false },
];

export const RULE_OPS_WITHOUT_VALUE = RULE_OPS.filter((o) => !o.needsValue).map((o) => o.value);

export const DEFAULT_COLUMN_STYLE = {
  align: 'left',
  weight: 'normal',
  ellipsis: true,
  color: null,
  background: null,
  display: 'text',
  chipVariant: 'neutral',
};

export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
export const DEFAULT_DATETIME_FORMAT = 'DD/MM/YYYY HH:mm';
